import type { I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';

import { faqItems as agentsFaqItems } from '../../components/agents/agentsPageContent';
import type { PublicFaqItem } from '../../components/home/homeFaqContent';
import { faqItems as researchBriefFaqItems } from '../../components/researchBriefs/researchBriefsContent';
import type { LocalizedMessage } from './messages';
import { resolveMessage } from './messages';
import {
    PUBLIC_SEO_DEFINITIONS,
    STRUCTURED_DATA_COPY,
    type PublicSeoDefinition,
    type PublicSocialImage,
} from './publicSeo';
import {
    getPublicWebSurface,
    isProductionPublicBuild,
    localizedPublicUrl,
    openGraphLocale,
    publicCanonicalUrl,
    publicRobotsPolicy,
    publicSeoAlternates,
    PUBLIC_WEB_CANONICAL_ORIGIN,
} from './publicWeb';

export interface MetaTagsProps {
    contentLocale?: string;
    title?: LocalizedMessage;
    description?: LocalizedMessage;
    image?: string;
    url?: string;
    canonicalUrl?: string;
    canonicalPathname?: string;
    surfaceId?: string;
    type?: 'website' | 'article';
    robots?: string;
    keywords?: LocalizedMessage;
    faqItems?: readonly PublicFaqItem[];
}

const DEFAULT_SOCIAL_IMAGE: PublicSocialImage = {
    url: `${PUBLIC_WEB_CANONICAL_ORIGIN}/static/logo-full-v6.png`,
    width: 1200,
    height: 630,
    alt: msg({
        id: 'seo.defaultImageAlt',
        message: 'Paperzilla mascot at a desk with a coffee and a printed brief, under the headline: Your research brief, ready with your coffee.',
        comment: 'Alt text for the default social sharing image used across public pages.',
    }),
};

function definitionForSurface(surfaceId: string | undefined): PublicSeoDefinition | null {
    if (!surfaceId || !Object.prototype.hasOwnProperty.call(PUBLIC_SEO_DEFINITIONS, surfaceId)) {
        return null;
    }
    return PUBLIC_SEO_DEFINITIONS[
        surfaceId as keyof typeof PUBLIC_SEO_DEFINITIONS
    ];
}

function resolvedFaqItems(i18n: I18n, items: readonly Pick<PublicFaqItem, 'question' | 'answer'>[]) {
    return items.map((item) => ({
        '@type': 'Question',
        name: resolveMessage(i18n, item.question),
        acceptedAnswer: {
            '@type': 'Answer',
            text: resolveMessage(i18n, item.answer),
        },
    }));
}

function homeStructuredData(
    i18n: I18n,
    canonicalUrl: string,
    faqItems: readonly PublicFaqItem[]
) {
    return [
        {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'Paperzilla',
            alternateName: 'Paperzilla AI',
            url: canonicalUrl,
        },
        {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'Paperzilla',
            url: PUBLIC_WEB_CANONICAL_ORIGIN,
            logo: `${PUBLIC_WEB_CANONICAL_ORIGIN}/static/logo.png`,
            sameAs: ['https://x.com/paperzilla_ai'],
        },
        {
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: 'Paperzilla',
            operatingSystem: 'Web',
            applicationCategory: 'BusinessApplication',
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
            url: canonicalUrl,
            description: resolveMessage(i18n, STRUCTURED_DATA_COPY.homeSoftwareDescription),
        },
        {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: resolvedFaqItems(i18n, faqItems),
        },
    ];
}

function agentsStructuredData(
    i18n: I18n,
    canonicalUrl: string,
    title: string,
    description: string
) {
    return [{
        '@context': 'https://schema.org',
        '@graph': [
            {
                '@type': 'WebPage',
                '@id': `${canonicalUrl}#webpage`,
                url: canonicalUrl,
                name: title,
                description,
                isPartOf: {
                    '@type': 'WebSite',
                    name: 'Paperzilla',
                    url: localizedPublicUrl('/', i18n.locale, {
                        allowTestLocale: i18n.locale === 'en-XA',
                    }),
                },
                about: [
                    {
                        '@type': 'Thing',
                        name: resolveMessage(i18n, STRUCTURED_DATA_COPY.agentsAboutAiAgents),
                    },
                    {
                        '@type': 'Thing',
                        name: resolveMessage(i18n, STRUCTURED_DATA_COPY.agentsAboutMonitoring),
                    },
                    {
                        '@type': 'Thing',
                        name: resolveMessage(i18n, STRUCTURED_DATA_COPY.agentsAboutMcp),
                    },
                ],
            },
            {
                '@type': 'FAQPage',
                '@id': `${canonicalUrl}#faq`,
                mainEntity: resolvedFaqItems(i18n, agentsFaqItems),
            },
        ],
    }];
}

export function resolvePageMetadata({
    contentLocale,
    title,
    description,
    image,
    url,
    canonicalUrl,
    canonicalPathname,
    surfaceId,
    type = 'website',
    robots,
    keywords,
    faqItems = [],
}: MetaTagsProps, i18n: I18n, messageLocale: string) {
    const locale = contentLocale ?? messageLocale;
    const definition = definitionForSurface(surfaceId);
    const titleValue = title ?? definition?.title;
    const descriptionValue = description ?? definition?.description;
    if (!titleValue || !descriptionValue) {
        throw new Error('MetaTags requires title and description or a registered SEO surface');
    }

    const resolvedTitle = resolveMessage(i18n, titleValue);
    const resolvedDescription = resolveMessage(i18n, descriptionValue);
    const fullTitle = resolvedTitle.toLowerCase().includes('paperzilla')
        ? resolvedTitle
        : resolveMessage(i18n, msg({
            id: 'seo.titleWithBrand',
            message: '{title} - Paperzilla',
            comment: 'Browser title wrapper. Paperzilla is the product brand.',
        }), { title: resolvedTitle });
    const allowTestLocale = locale === 'en-XA';
    const emitProductionMetadata = !surfaceId || isProductionPublicBuild();
    const resolvedCanonicalUrl = surfaceId
        ? publicCanonicalUrl(surfaceId, locale, canonicalPathname, { allowTestLocale })
        : canonicalUrl || url || PUBLIC_WEB_CANONICAL_ORIGIN;
    const resolvedPageUrl = surfaceId && !emitProductionMetadata
        ? null
        : surfaceId ? resolvedCanonicalUrl : url || resolvedCanonicalUrl;
    const socialImage = definition?.image ?? DEFAULT_SOCIAL_IMAGE;
    const resolvedImageInput = image ?? socialImage.url;
    // A caller-supplied image has unknown dimensions and no alt text.
    const resolvedImageDetails = image ? null : {
        width: socialImage.width,
        height: socialImage.height,
        alt: resolveMessage(i18n, socialImage.alt),
    };
    const resolvedImage = (() => {
        try {
            return new URL(resolvedImageInput, resolvedCanonicalUrl).toString();
        } catch {
            return resolvedImageInput;
        }
    })();
    const resolvedRobots = surfaceId
        ? publicRobotsPolicy(surfaceId, locale, robots ?? definition?.robots)
        : robots;
    const resolvedKeywordsValue = keywords ?? definition?.keywords;
    const resolvedKeywords = resolvedKeywordsValue
        ? resolveMessage(i18n, resolvedKeywordsValue)
        : null;
    const alternates = surfaceId && (!contentLocale || contentLocale === messageLocale)
        ? publicSeoAlternates(surfaceId, canonicalPathname, locale)
        : [];
    const structuredDataKind = definition?.structuredData;
    const structuredData = !emitProductionMetadata
        ? []
        : structuredDataKind === 'home'
        ? homeStructuredData(i18n, resolvedCanonicalUrl, faqItems)
        : structuredDataKind === 'agents'
            ? agentsStructuredData(i18n, resolvedCanonicalUrl, fullTitle, resolvedDescription)
            : structuredDataKind === 'research-briefs'
                ? [{
                    '@context': 'https://schema.org',
                    '@graph': [
                        {
                            '@type': 'WebPage',
                            '@id': `${resolvedCanonicalUrl}#webpage`,
                            url: resolvedCanonicalUrl,
                            name: fullTitle,
                            description: resolvedDescription,
                            inLanguage: locale,
                            isPartOf: { '@type': 'WebSite', name: 'Paperzilla', url: PUBLIC_WEB_CANONICAL_ORIGIN },
                        },
                        {
                            '@type': 'FAQPage',
                            '@id': `${resolvedCanonicalUrl}#faq`,
                            mainEntity: resolvedFaqItems(i18n, researchBriefFaqItems),
                        },
                    ],
                }]
            : [];
    const otherOpenGraphLocales = alternates
        .filter(({ hrefLang }) => hrefLang !== locale && hrefLang !== 'x-default')
        .map(({ hrefLang }) => openGraphLocale(hrefLang));

    if (surfaceId) {
        getPublicWebSurface(surfaceId);
    }

    return { fullTitle, emitProductionMetadata, resolvedCanonicalUrl, alternates, structuredData, structuredDataKind, resolvedDescription, resolvedKeywords, resolvedRobots, resolvedImage, resolvedImageDetails, resolvedPageUrl, type, surfaceId, locale, otherOpenGraphLocales };
}
